import { useCallback, useEffect, useRef, useState } from "react";

import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";
import { logger } from "~/services/logger";
import { getErrorMessage } from "@repo/system";

import { type CodeValidationResult, validate } from "./validate";
import { cleanup } from "./cleanup";

type ScannerState = {
  status: "idle" | "pending" | "success" | "error";
  error: unknown;
  data: CodeValidationResult | null;
};

type UseScannerOptions = {
  onSuccess?: (data: CodeValidationResult) => void;
  onError?: (error: unknown) => void;
  enabled?: boolean;
  timeout?: number;
};

type UseScannerReturn = ScannerState & {
  reset: () => void;
};

const DEFAULT_TIMEOUT_MS = 200;

/**
 * A hook for scanning and validating codes, similar to TanStack Query's useMutation.
 * It provides loading states, error handling, and success callbacks.
 *
 * @param options Configuration options for the scanner
 * @returns Scanner state and actions
 *
 * @example
 * ```tsx
 * const scanner = useScanner({
 *   onSuccess: (result) => {
 *     console.log('Scanned:', result);
 *   },
 *   onError: (error) => {
 *     console.error('Scan failed:', error);
 *   }
 * });
 *
 * // Check states
 * if (scanner.status === "pending") return <div>Scanning...</div>;
 * if (scanner.status === "error") return <div>Error: {scanner.error.message}</div>;
 * if (scanner.status === "success") return <div>Scanned: {scanner.data.value}</div>;
 * ```
 */
export const useScanner = ({
  onSuccess,
  onError,
  enabled = true,
  timeout = DEFAULT_TIMEOUT_MS
}: UseScannerOptions = {}): UseScannerReturn => {
  const [state, setState] = useState<ScannerState>({
    status: "idle",
    error: null,
    data: null
  });

  const payload = useRef<string>("");
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isProcessingRef = useRef(false);

  const clearPayload = useCallback(() => {
    payload.current = "";
  }, []);

  const handleError = useCallback(
    (error: unknown) => {
      logger.warn(`[Scanner] Error scanning code - ${getErrorMessage(error)}`, {
        scan_code_payload: payload.current
      });

      setState({
        status: "error",
        error,
        data: null
      });
      onError?.(error);
      clearPayload();
      isProcessingRef.current = false;
    },
    [onError, clearPayload]
  );

  const handleScan = useCallback(
    (code: string) => {
      if (!code.trim()) {
        handleError(new Error("Empty code"));
        return;
      }

      if (isProcessingRef.current) {
        logger.debug("[Scanner] Already processing, ignoring new scan", {
          scan_code_payload: payload.current
        });
        return; // Prevent multiple simultaneous scans
      }

      logger.debug("[Scanner] Processing new code", {
        scan_code_payload: code
      });

      isProcessingRef.current = true;
      setState({
        status: "pending",
        error: null,
        data: null
      });

      try {
        const cleanedCode = cleanup(code);
        if (cleanedCode !== code) {
          logger.debug("[Scanner] Code cleaned up", {
            scan_code_payload: code,
            scan_code_cleaned: cleanedCode
          });
        }

        const result = validate(cleanedCode);
        if (!result) {
          logger.warn("[Scanner] Validation failed - no matching format", {
            scan_code_payload: code,
            scan_code_cleaned: cleanedCode
          });
          throw new DisplayError(DisplayErrorCodeEnum.SCANNER_INVALID_CODE);
        }

        logger.debug("[Scanner] Validation successful", {
          scan_code_payload: code,
          scan_code_cleaned: cleanedCode,
          scan_code_value: result.value,
          scan_code_format: result.format,
          scan_code_patterns: result.patterns
        });

        setState({
          status: "success",
          error: null,
          data: result
        });

        onSuccess?.(result);
        isProcessingRef.current = false;
      } catch (error) {
        logger.warn("[Scanner] Validation error", {
          scan_code_payload: code
        });
        handleError(error);
      }
    },
    [onSuccess, handleError]
  );

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (payload.current && !isProcessingRef.current) {
        handleScan(payload.current);
        clearPayload();
      }
    }, timeout);
  }, [timeout, clearPayload, handleScan]);

  const reset = useCallback(() => {
    setState({
      status: "idle",
      error: null,
      data: null
    });
    clearPayload();
    isProcessingRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [clearPayload]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      try {
        // Ignore key events when user is typing in an input/textarea/select or contenteditable element
        const activeElement = document.activeElement;
        if (
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.tagName === "SELECT" ||
            (activeElement instanceof HTMLElement &&
              activeElement.isContentEditable))
        ) {
          return;
        }

        // `Clear` will reset the payload
        if (event.key === "Clear") {
          clearPayload();
          return;
        }

        // `Enter` or `Shift` when scanning is finished
        if (
          event.key === "Enter" ||
          event.key === "Shift" ||
          event.key === "Tab"
        ) {
          event.preventDefault();
          return;
        }

        payload.current = payload.current.concat(event.key);
        resetTimeout();
      } catch (error) {
        handleError(error);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, clearPayload, resetTimeout, handleError]);

  return {
    ...state,
    reset
  };
};
